import { useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { X, AlertTriangle, Trash2, ArrowDown, Loader2 } from "lucide-react";

/**
 * Two-branch cancel flow.
 * - "Downgrade" → keeps the account for 12 months, cancels Stripe renewal.
 * - "Delete"   → wipes everything server-side.
 */
export default function CancelAccountModal({ onClose }) {
  const { t } = useT();
  const [mode, setMode] = useState(null); // null | "downgrade" | "delete"
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const doDowngrade = async () => {
    setBusy(true);
    try {
      await api.post("/account/downgrade", { reason, note });
      toast.success(t("cancelDowngradeDone"));
      onClose?.();
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.post("/account/delete");
      toast.success(t("cancelDeleteDone"));
      // Force logout — the session token no longer resolves.
      localStorage.clear();
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="cancel-account-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F9F8F6] border border-neutral-900 w-full max-w-xl my-8"
      >
        <div className="flex items-start justify-between p-5 border-b border-neutral-300">
          <div>
            <p className="ed-eyebrow">{t("cancelEyebrow")}</p>
            <h2 className="font-serif text-2xl mt-1">{t("cancelTitle")}</h2>
          </div>
          <button data-testid="cancel-modal-close" onClick={onClose} className="p-1.5 hover:bg-neutral-100"><X size={16} /></button>
        </div>

        {mode === null && (
          <div className="p-5 space-y-3">
            <button
              type="button"
              data-testid="cancel-choose-downgrade"
              onClick={() => setMode("downgrade")}
              className="w-full text-left border border-neutral-300 hover:border-neutral-900 p-4 transition-colors"
            >
              <div className="flex items-start gap-3">
                <ArrowDown size={18} className="text-neutral-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-serif text-lg">{t("cancelDowngradeTitle")}</p>
                  <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
                    {t("cancelDowngradeBody")}
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              data-testid="cancel-choose-delete"
              onClick={() => setMode("delete")}
              className="w-full text-left border border-rose-300 hover:border-rose-700 p-4 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Trash2 size={18} className="text-rose-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-serif text-lg text-rose-800">{t("cancelDeleteTitle")}</p>
                  <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
                    {t("cancelDeleteBody")}
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {mode === "downgrade" && (
          <div className="p-5 space-y-4">
            <p className="text-sm text-neutral-700 leading-relaxed">
              {t("cancelDowngradeBody")}
            </p>
            <div>
              <label className="ed-label">{t("cancelReasonLabel")}</label>
              <select
                data-testid="cancel-downgrade-reason"
                className="ed-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="">—</option>
                <option value="job_found">{t("cancelReasonJobFound")}</option>
                <option value="too_little_use">{t("cancelReasonTooLittle")}</option>
                <option value="other_expectations">{t("cancelReasonExpect")}</option>
                <option value="other">{t("cancelReasonOther")}</option>
              </select>
            </div>
            <div>
              <label className="ed-label">{t("cancelNoteLabel")}</label>
              <textarea
                data-testid="cancel-downgrade-note"
                rows={3}
                maxLength={500}
                className="ed-input w-full"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button data-testid="cancel-downgrade-back" onClick={() => setMode(null)} className="ed-btn ed-btn-outline">
                {t("cancel")}
              </button>
              <button data-testid="cancel-downgrade-confirm" onClick={doDowngrade} disabled={busy} className="ed-btn">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowDown size={13} strokeWidth={1.7} />}
                {t("cancelDowngradeConfirm")}
              </button>
            </div>
          </div>
        )}

        {mode === "delete" && (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3 border border-rose-300 bg-rose-50 p-4">
              <AlertTriangle size={18} className="text-rose-700 shrink-0 mt-0.5" />
              <p className="text-sm text-rose-900 leading-relaxed">{t("cancelDeleteWarning")}</p>
            </div>
            <div>
              <label className="ed-label">
                {t("cancelDeleteConfirmLabel", { word: "VERWIJDER" })}
              </label>
              <input
                data-testid="cancel-delete-confirm-input"
                className="ed-input"
                placeholder="VERWIJDER"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button data-testid="cancel-delete-back" onClick={() => setMode(null)} className="ed-btn ed-btn-outline">
                {t("cancel")}
              </button>
              <button
                data-testid="cancel-delete-confirm"
                onClick={doDelete}
                disabled={busy || confirmText.trim() !== "VERWIJDER"}
                className="ed-btn bg-rose-700 !text-white border-rose-700 hover:bg-rose-800 hover:border-rose-800 disabled:opacity-40"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} strokeWidth={1.7} />}
                {t("cancelDeleteConfirm")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
